import { createBrowserRouter, RouterProvider, Navigate, useLocation } from 'react-router-dom';
import AuthGuard from './components/auth/AuthGuard';
import LayoutNew from './components/layout/LayoutNew';
import TenantLayout from './components/layout/TenantLayout';
import TenantSelectPage from './pages/TenantSelectPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import DashboardPage from './pages/DashboardPage';
import FilesPage from './pages/FilesPage';
import FileDetailsPage from './pages/FileDetailsPage';
import CategoriesPageNew from './pages/CategoriesPageNew';
import TagsPage from './pages/TagsPage';
import TrashPage from './pages/TrashPage';
import HelpPage from './pages/HelpPage';
import SettingsPage from './pages/SettingsPage';
import PublicPortalPageNew from './pages/PublicPortalPageNew';
import UserManagementPage from './pages/UserManagementPage';
import OrdersPage from './pages/OrdersPage';
import FaqSpacesManagementPage from './pages/FaqSpacesManagementPage';

// 产品矩阵系统
import { PosterLayout } from './poster/PosterLayout';
import { PosterCanvasPage } from './poster/pages/PosterCanvasPage';
// 使用模块化版本 v4.2.0 - 包含 useTransition、React.memo 等性能优化
import { ProductManagePage } from './poster/pages/ProductManagePage/index';
import { ProductHierarchyPage } from './poster/pages/ProductHierarchyPage';
import { ProductSelectionWizard } from './poster/pages/ProductSelectionWizard';

// 白板系统
import { WhiteboardLayout } from './whiteboard/WhiteboardLayout';
import { WhiteboardListPage } from './whiteboard/pages/WhiteboardListPage';
// WhiteboardEditorPage 由 KeepAliveEditorContainer 内部管理，不再直接路由渲染
import {
  PriceIntelDashboardPage,
  DocHubStudioPage,
  PriceIntelLayout,
  IntelDatabasePage,
  IntelAnalyticsPage,
  IntelReportsPage,
} from './price_intel';


// FAQ 知识中心
import {
  FaqLayout,
  FaqAdminPage,
  FaqTagsPage,
  FaqUsersPage,
  FaqSupportHomePage,
  FaqPublicPage,
  FaqDetailPage,
  FaqTrashPage,
  FaqQuestionsPage,
  FaqQuestionDetailPage,
  FaqQuestionAdminPage,
} from './faq';

function FaqAdminLegacyRedirect() {
  const location = useLocation();
  const nextPath = location.pathname.replace(/^\/uadmin\/faq/, '/faq/uadmin');
  return <Navigate to={`${nextPath}${location.search}${location.hash}`} replace />;
}

const tenantChildren = [
  {
    path: 'login',
    element: <LoginPage />
  },
  {
    path: 'register',
    element: <RegisterPage />
  },
  {
    path: 'reset-password',
    element: <ResetPasswordPage />
  },
  {
    path: 'portal/:token?',
    element: <PublicPortalPageNew />
  },
  // 默认重定向到门户
  {
    index: true,
    element: <Navigate to="portal" replace />
  },
];

const router = createBrowserRouter([
  // 根路径：租户选择页
  {
    path: '/',
    element: <TenantSelectPage />
  },

  // 多租户路由：显式租户，避免顶层 FAQ Space slug 被拦截
  {
    path: '/cn',
    element: <TenantLayout tenantId="cn" />,
    children: tenantChildren,
  },
  {
    path: '/intl',
    element: <TenantLayout tenantId="intl" />,
    children: tenantChildren,
  },

  // 兼容旧路由：重定向到租户选择或默认租户
  {
    path: '/login',
    element: <Navigate to="/cn/login" replace />
  },
  {
    path: '/register',
    element: <Navigate to="/cn/register" replace />
  },
  {
    path: '/portal/:token?',
    element: <Navigate to="/cn/portal" replace />
  },

  // 产品矩阵系统路由
  {
    path: '/poster',
    element: (
      <AuthGuard>
        <PosterLayout />
      </AuthGuard>
    ),
    children: [
      {
        index: true,
        element: <PosterCanvasPage />,
      },
      {
        path: 'products',
        element: <ProductManagePage />,
      },
      {
        path: 'hierarchy',
        element: <ProductHierarchyPage />,
      },
      {
        path: 'wizard',
        element: <ProductSelectionWizard />,
      },
    ],
  },

  // 白板系统路由 — 编辑器由 KeepAliveEditorContainer 管理，不通过 Outlet
  {
    path: '/whiteboard',
    element: (
      <AuthGuard>
        <WhiteboardLayout />
      </AuthGuard>
    ),
    children: [
      {
        index: true,
        element: <WhiteboardListPage />,
      },
      {
        path: ':id',
        // KeepAlive 模式：编辑器由 Layout 内的 KeepAliveEditorContainer 渲染
        // 此路由仅用于让 Layout 检测到编辑器路由，Outlet 不渲染任何内容
        element: null,
      },
    ],
  },

  // 竞品情报平台 — 4 个核心页面
  {
    path: '/price_intel',
    element: (
      <AuthGuard>
        <PriceIntelLayout />
      </AuthGuard>
    ),
    children: [
      { index: true, element: <PriceIntelDashboardPage /> },          // 录入
      { path: 'doc_hub', element: <DocHubStudioPage /> },             // 📄 文档中枢 (Doc Hub)
      { path: 'database', element: <IntelDatabasePage /> },            // 数据库

      { path: 'analytics', element: <IntelAnalyticsPage /> },          // 分析
      { path: 'reports', element: <IntelReportsPage /> },              // AI 报告
      // 旧路由兼容 → 重定向
      { path: 'reviews', element: <Navigate to="/price_intel" replace /> },
      { path: 'facts', element: <Navigate to="/price_intel/database" replace /> },
      { path: 'products', element: <Navigate to="/price_intel/database" replace /> },
      { path: 'pitch', element: <Navigate to="/price_intel/database" replace /> },
      { path: 'competitors', element: <Navigate to="/price_intel/database" replace /> },
      { path: 'trends', element: <Navigate to="/price_intel/analytics" replace /> },
      { path: 'briefings', element: <Navigate to="/price_intel/reports" replace /> },
      { path: 'jobs', element: <Navigate to="/price_intel" replace /> },
    ],
  },

  // 管理后台（保持不变）
  {
    path: '/uadmin',
    element: (
      <AuthGuard>
        <LayoutNew />
      </AuthGuard>
    ),
    children: [
      {
        index: true,
        element: <DashboardPage />,
      },
      {
        path: 'files',
        element: <FilesPage />,
      },
      {
        path: 'files/:id',
        element: <FileDetailsPage />,
      },
      {
        path: 'categories/:id?',
        element: <CategoriesPageNew />,
      },
      {
        path: 'tags',
        element: <TagsPage />,
      },
      {
        path: 'settings/:section?',
        element: <SettingsPage />,
      },
      {
        path: 'starred',
        element: <FilesPage />,
      },
      {
        path: 'recent',
        element: <FilesPage />,
      },
      {
        path: 'trash',
        element: <TrashPage />,
      },
      {
        path: 'help',
        element: <HelpPage />,
      },
      {
        path: 'users',
        element: <UserManagementPage />,
      },
      {
        path: 'orders',
        element: <OrdersPage />,
      },
      {
        path: 'faq-spaces',
        element: <FaqSpacesManagementPage />,
      },
    ],
  },

  // FAQ 知识中心 — 管理面板（数据库 FAQ Space 驱动）
  {
    path: '/:faqSpaceSlug/uadmin',
    element: (
      <AuthGuard>
        <FaqLayout mode="admin" />
      </AuthGuard>
    ),
    children: [
      { index: true, element: <FaqAdminPage /> },
      { path: 'questions', element: <FaqQuestionAdminPage /> },
      { path: 'tags', element: <FaqTagsPage /> },
      { path: 'users', element: <FaqUsersPage /> },
      { path: 'trash', element: <FaqTrashPage /> },
    ],
  },
  {
    path: '/uadmin/faq/*',
    element: <FaqAdminLegacyRedirect />,
  },

  // FAQ 知识中心 — 用户端公开页面（/faq、/sql-faq 等顶层 Space slug）
  {
    path: '/:faqSpaceSlug',
    element: (
      <AuthGuard>
        <FaqLayout mode="public" />
      </AuthGuard>
    ),
    children: [
      { index: true, element: <FaqSupportHomePage /> },
      { path: 'knowledge', element: <FaqPublicPage /> },
      { path: 'questions', element: <FaqQuestionsPage /> },
      { path: 'questions/:questionId', element: <FaqQuestionDetailPage /> },
      { path: ':id', element: <FaqDetailPage /> },
    ],
  },
], {
  future: {
    v7_relativeSplatPath: true,
    v7_fetcherPersist: true,
    v7_normalizeFormMethod: true,
    v7_partialHydration: true,
    v7_skipActionErrorRevalidation: true,
  },
});

function App() {
  return <RouterProvider router={router} future={{ v7_startTransition: true }} />;
}

export default App;
